import { Empty, Modal, Switch, Table, Tag, Tooltip, notification } from "antd";
import { get, isEmpty, isNil } from "lodash";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiPlus } from "react-icons/bi";
import {
  MdHistory,
  MdOutlineDeleteOutline,
  MdOutlineEdit,
} from "react-icons/md";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Pagination from "shared/components/Pagination";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import { COLORS } from "shared/style/colors";
import graphql from "utils/api/graphql";
import { INITIAL_VALUES } from "utils/constants";
import { notifyFailure, run } from "../Organizations/Detail/hooks";
import DispatcherDrawer, { TABS } from "./DispatcherDrawer";

const View = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DISPATCHER);
  const { user } = useContext(StoreContext);
  const organizationId = get(user, "organization.id");
  const names = { organization: get(user, "organization.name", "") };
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState(INITIAL_VALUES);
  const [drawer, setDrawer] = useState(null);

  const dispatchersQuery = useQuery(graphql.queries.DISPATCHERS, {
    fetchPolicy: "network-only",
    variables: {
      ...pagination,
      ...(isEmpty(search) ? {} : { like: { name: `%${search}%` } }),
    },
    skip: isEmpty(organizationId),
  });
  const [updateDispatcher] = useMutation(graphql.mutations.UPDATE_DISPATCHER);
  const [deleteDispatcher] = useMutation(graphql.mutations.DELETE_DISPATCHER);
  const onFailure = notifyFailure({
    ERRORS: t("ERRORS", { returnObjects: true }),
  });
  const refetch = () => dispatchersQuery.refetch();

  const toggle = ({ id }, active) =>
    run(updateDispatcher, { variables: { where: { id }, data: { active } } })
      .then(refetch)
      .catch(onFailure);

  const rows = get(dispatchersQuery, "data.dispatchers.data") || [];
  const afterDelete = () =>
    rows.length === 1 && pagination.skip > 0
      ? setPagination((previous) => ({
          ...previous,
          skip: Math.max(0, previous.skip - previous.take),
        }))
      : refetch();

  const remove = ({ id, name }) =>
    Modal.confirm({
      title: t("DELETE.TITLE", { name }),
      content: t("DELETE.TEXT"),
      okText: t("DELETE.CONFIRM"),
      cancelText: t("DELETE.CANCEL"),
      okButtonProps: { danger: true },
      onOk: () =>
        run(deleteDispatcher, { variables: { where: { id } } })
          .then(() => {
            notification.success({ message: t("DELETE.DONE") });
            return afterDelete();
          })
          .catch(onFailure),
    });

  const header = (
    <Header
      title={t("TITLE")}
      subtitle={t("SUBTITLE", names)}
      actions={
        isEmpty(organizationId)
          ? []
          : [
              {
                icon: <BiPlus size={20} />,
                text: t("CREATE"),
                onClick: () =>
                  setDrawer({ dispatcher: null, tab: TABS.SETTINGS }),
              },
            ]
      }
    />
  );

  if (isEmpty(organizationId))
    return (
      <>
        {header}
        <Card type="simple">
          <Empty description={t("NO_ORGANIZATION")} />
        </Card>
      </>
    );

  const columns = [
    {
      title: t("COLUMNS.NAME"),
      dataIndex: "name",
    },
    {
      title: t("COLUMNS.SOURCES"),
      dataIndex: "sources",
      render: (sources) =>
        (sources || []).map((source) => <Tag key={source}>{source}</Tag>),
    },
    {
      title: t("COLUMNS.ROUTES"),
      dataIndex: "routes",
      render: (routes) =>
        (routes || []).map((route) => (
          <Tag key={route} color="blue">
            {t(`ROUTE.${route}`)}
          </Tag>
        )),
    },
    {
      title: t("COLUMNS.SPLIT"),
      dataIndex: "targets",
      render: (targets) =>
        (targets || [])
          .map(
            ({ organization, share }) =>
              `${get(organization, "name", t("EMPTY_VALUE"))} ${share} %`,
          )
          .join(" · "),
    },
    {
      title: t("COLUMNS.ACTIVE"),
      dataIndex: "active",
      render: (active, dispatcher) => (
        <Switch
          checked={active}
          onChange={(checked) => toggle(dispatcher, checked)}
        />
      ),
    },
    {
      title: t("COLUMNS.ACTIONS"),
      key: "actions",
      align: "right",
      render: (_value, dispatcher) => (
        <div className="d-flex justify--end dispatchers__icons">
          <Tooltip title={t("ACTIONS.EDIT")}>
            <MdOutlineEdit
              size={22}
              color={COLORS.C_PRIMARY}
              onClick={() => setDrawer({ dispatcher, tab: TABS.SETTINGS })}
            />
          </Tooltip>
          <Tooltip title={t("ACTIONS.LOGS")}>
            <MdHistory
              size={22}
              color={COLORS.C_PRIMARY}
              onClick={() => setDrawer({ dispatcher, tab: TABS.LOGS })}
            />
          </Tooltip>
          <Tooltip title={t("ACTIONS.DELETE")}>
            <MdOutlineDeleteOutline
              size={22}
              color={COLORS.C_DANGER}
              onClick={() => remove(dispatcher)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      {header}
      <Card type="simple">
        <div className="grid--4 m-bottom-24">
          <Input
            type="search"
            placeholder={t("SEARCH")}
            onChange={({ target: { value } }) => {
              setPagination(INITIAL_VALUES);
              setSearch(value);
            }}
          />
        </div>
        <Table
          dataSource={rows}
          columns={columns}
          loading={dispatchersQuery.loading}
          locale={{ emptyText: t("EMPTY") }}
          className="table__wrapper"
          rowKey="id"
          pagination={false}
          scroll={{ x: true }}
        />
        <Pagination
          count={get(dispatchersQuery, "data.dispatchers.count", 0)}
          setValues={setPagination}
          skip={pagination.skip}
        />
      </Card>
      {isNil(drawer) ? null : (
        <DispatcherDrawer
          dispatcher={drawer.dispatcher}
          tab={drawer.tab}
          names={names}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            setDrawer(null);
            return refetch();
          }}
        />
      )}
    </>
  );
};

export default View;
